import {Fragment, useContext, useEffect, useMemo, useState} from 'react';

import {Route, Routes, useLocation} from 'react-router-dom';

import {createTheme, CssBaseline, GlobalStyles, ThemeProvider} from '@mui/material';
import './scripts/hotjar';

import {BackgroundGradient, BackgroundVariant} from './components/BackgroundGradient/BackgroundGradient';
import {ErrorMessage} from './components/ErrorMessage/ErrorMessage';
import {LoadingOverlay} from './components/Loading/LoadingOverlay';
import {PopupMessage} from './components/PopupMessage/PopupMessage';
import {FeatureFlagContext} from './contexts/FeatureFlagContext/FeatureFlagContext';
import {LanguageProvider} from './contexts/LanguageContext/LanguageProvider';
import {LoadingProvider} from './contexts/LoadingContext/LoadingProvider';
import {SnackBarProvider} from './contexts/SnackBarContext/SnackBarProvider';
import {UserContext} from './contexts/UserContext/UserContext';
import {MaintenanceMode} from './layouts/MaintenanceMode';
import {getPalette} from './layouts/theme/EmpionTheme';
import {getScrollbarGlobalStyles} from './layouts/theme/ThemeHelpers';
import {CultureAnalysisPdfPage} from './pages/company/culture/CultureAnalysisPdfPage/CultureAnalysisPdfPage';
import {DashboardPage} from './pages/user/dashboard/DashboardPage';
import ProfilePage from './pages/user/profile/ProfilePage';
import {ConfirmAccountPage} from './pages/user/registration/ConfirmAccountPage';
import {ApplicantRoutes} from './Routes/ApplicantRoutes';
import {CompanyRoutes} from './Routes/CompanyRoutes/CompanyRoutes';
import {PublicRoutes} from './Routes/PublicRoutes';
import {AccountConfirmedGuard} from './Routes/RouteGuards/AccountConfirmedGuard';
import {LoggedInGuard} from './Routes/RouteGuards/LoggedInGuard';
import {UserAccountRoutes} from './Routes/UserAccountRoutes';

function App() {
  const currentPath = useLocation().pathname;

  const {flags} = useContext(FeatureFlagContext);
  const path = useLocation().pathname;

  const [globalStyles, setGlobalStyles] = useState({body: {}});
  const theme = useMemo(() => createTheme(getPalette()), []);
  const scrollbarGlobalStyles = useMemo(() => getScrollbarGlobalStyles(), []);

  const {isUserLoggedIn, isLoading} = useContext(UserContext);
  const isPdfPage = currentPath.startsWith('/culture-analysis-pdf');

  useEffect(() => {
    if (isLoading) return;
    if (!isUserLoggedIn() && !isPdfPage) {
      setGlobalStyles({
        body: {
          backgroundImage: 'none',
        },
      });
    }
  }, [isUserLoggedIn, currentPath, isLoading, isPdfPage]);

  const setBackgroundVariant = (): BackgroundVariant => {
    if (isUserLoggedIn()) {
      return BackgroundVariant.LoggedIn;
    }

    if (path === '/' || path.includes('password')) {
      return BackgroundVariant.PasswordReset;
    }
    return BackgroundVariant.LoggedOut;
  };

  return (
    <Fragment>
      <GlobalStyles styles={{...scrollbarGlobalStyles, ...globalStyles}} />
      <ThemeProvider theme={theme}>
        <SnackBarProvider>
          <LanguageProvider>
            <LoadingProvider>
              <CssBaseline />
              <BackgroundGradient variant={setBackgroundVariant()} />
              {flags.MAINTENANCE_MODE ? (
                <MaintenanceMode />
              ) : (
                <Routes>
                  <Route path='/*' element={<PublicRoutes />} />
                  <Route path='/company/*' element={<CompanyRoutes />} />
                  <Route element={<LoggedInGuard />}>
                    <Route path='/confirm-email' element={<ConfirmAccountPage />} />
                    <Route path='/account/*' element={<UserAccountRoutes />} />
                    <Route element={<AccountConfirmedGuard />}>
                      <Route path='/applicant/*' element={<ApplicantRoutes />} />
                      <Route path='/profile' element={<ProfilePage />} />
                      <Route path='/dashboard' element={<DashboardPage />} />
                    </Route>
                  </Route>
                  <Route path='culture-analysis-pdf/:resourceId' element={<CultureAnalysisPdfPage />} />
                  <Route path='*' element={<ErrorMessage isOutOfBounds />} />
                </Routes>
              )}
              <PopupMessage />
              <LoadingOverlay />
            </LoadingProvider>
          </LanguageProvider>
        </SnackBarProvider>
      </ThemeProvider>
    </Fragment>
  );
}

export default App;
